import { IconButton } from "@/components/ui/icon-button";
import { InlineIconButton } from "@/components/ui/pressable";
import { useRef, type ReactNode } from "react";
import { Bold, Italic, Link2, List, Paperclip, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RAFile } from "@/components/atomic-crm/types";
import { MemberMentionEditor } from "@/components/atomic-crm/tasks/MemberMentionEditor";

/** Files picked in the composer; uploaded by the note save hook. */
export type ThreadDraftFile = RAFile & { rawFile: File };

const insertText = (text: string) =>
  document.execCommand("insertText", false, text);

/** Wraps the selection (or inserts an empty pair with the caret inside). */
const wrapSelection = (marker: string) => {
  const selection = window.getSelection();
  const selected = selection?.toString() ?? "";
  if (selected) {
    insertText(`${marker}${selected}${marker}`);
    return;
  }
  insertText(`${marker}${marker}`);
  for (let index = 0; index < marker.length; index += 1) {
    selection?.modify?.("move", "backward", "character");
  }
};

const ToolbarButton = ({
  label,
  onAction,
  children,
}: {
  label: string;
  onAction: () => void;
  children: ReactNode;
}) => (
  <IconButton
    className="size-7 min-h-7 min-w-7"
    aria-label={label}
    title={label}
    // Keep the editor's selection: act on mousedown, never take focus.
    onMouseDown={(event) => {
      event.preventDefault();
      onAction();
    }}
  >
    {children}
  </IconButton>
);

/**
 * Note editor for threads: @mentions, a small formatting bar (bold,
 * italic, bullets, link) and optional file attachments.
 */
export const ThreadEditor = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder,
  ariaLabel,
  rows = 2,
  autoFocus,
  files,
  onFilesChange,
  actions,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit?: () => void;
  /** Esc closes the editor without saving. */
  onCancel?: () => void;
  placeholder?: string;
  ariaLabel: string;
  rows?: number;
  autoFocus?: boolean;
  /** Omit to hide attachments (e.g. when editing). */
  files?: ThreadDraftFile[];
  onFilesChange?: (next: ThreadDraftFile[]) => void;
  /** Right side of the toolbar (Post / Send / Save). */
  actions?: ReactNode;
  className?: string;
}) => {
  const fileInput = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    if (!list || !onFilesChange) return;
    onFilesChange([
      ...(files ?? []),
      ...Array.from(list).map((file) => ({
        rawFile: file,
        src: URL.createObjectURL(file),
        title: file.name,
      })),
    ]);
  };

  return (
    <div
      className={cn(
        "rounded-xl border bg-background focus-within:ring-2 focus-within:ring-ring/30",
        className,
      )}
    >
      <MemberMentionEditor
        value={value}
        onChange={onChange}
        onSubmit={onSubmit}
        onCancel={onCancel}
        rows={rows}
        autoFocus={autoFocus}
        ariaLabel={ariaLabel}
        placeholder={placeholder}
        className="border-0 bg-transparent shadow-none focus-visible:ring-0"
      />
      {files?.length ? (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {files.map((file, index) => (
            <span
              key={`${file.title}-${index}`}
              className="inline-flex max-w-56 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
            >
              <Paperclip className="size-3 shrink-0" />
              <span className="truncate">{file.title}</span>
              <InlineIconButton
                size="xs"
                aria-label={`Remove ${file.title}`}
                onClick={() =>
                  onFilesChange?.(files.filter((_, item) => item !== index))
                }
              >
                <X className="size-3" />
              </InlineIconButton>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-0.5 border-t px-1.5 py-1">
        <ToolbarButton label="Bold" onAction={() => wrapSelection("**")}>
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton label="Italic" onAction={() => wrapSelection("*")}>
          <Italic className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton label="Bullet list" onAction={() => insertText("\n- ")}>
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Link"
          onAction={() => {
            const selected = window.getSelection()?.toString() ?? "";
            const url = window.prompt("Link URL", "https://");
            if (!url || url === "https://") return;
            insertText(`[${selected || url}](${url})`);
          }}
        >
          <Link2 className="size-3.5" />
        </ToolbarButton>
        {onFilesChange ? (
          <>
            <ToolbarButton
              label="Attach files"
              onAction={() => fileInput.current?.click()}
            >
              <Paperclip className="size-3.5" />
            </ToolbarButton>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </>
        ) : null}
        <div className="ml-auto flex items-center gap-2">{actions}</div>
      </div>
    </div>
  );
};
